import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Calendar as GCalIcon,
  CheckCircle2,
  Link2,
  Unlink,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCalendar({ token, onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ connected: false });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/status`, authHeaders);
      setStatus(data);
    } catch (e) {
      if (e.response?.status === 401 && onSessionExpired) return onSessionExpired();
      toast.error('Error al comprobar el estado de Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // React to redirect back from Google
    const params = new URLSearchParams(window.location.search);
    const cal = params.get('calendar');
    if (cal === 'connected') {
      toast.success('¡Google Calendar conectado correctamente!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (cal === 'error') {
      const reason = params.get('reason') || 'desconocido';
      const messages = {
        missing_calendar_scope: 'No has concedido permiso a Google Calendar. Vuelve a intentarlo y MARCA la casilla "Ver, editar, compartir y eliminar permanentemente todos los calendarios".',
        no_refresh_token: 'Google no envió el token de renovación. Ve a https://myaccount.google.com/permissions, revoca "Spanisch en la red" y vuelve a conectar.',
        exchange_failed: 'Error al intercambiar el código con Google. Revisa que las URIs de redirección estén correctas.',
        access_denied: 'Cancelaste la autorización.',
      };
      toast.error(messages[reason] || `Error al conectar Google Calendar: ${reason}`, { duration: 10000 });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/calendar/auth/url`, authHeaders);
      window.location.href = data.authorization_url;
    } catch (e) {
      if (e.response?.status === 401 && onSessionExpired) return onSessionExpired();
      toast.error('No se pudo iniciar la conexión con Google');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Seguro que quieres desconectar Google Calendar? Las nuevas reservas ya no crearán eventos.')) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/calendar/disconnect`, {}, authHeaders);
      toast.success('Google Calendar desconectado');
      setStatus({ connected: false });
    } catch (e) {
      if (e.response?.status === 401 && onSessionExpired) return onSessionExpired();
      toast.error('Error al desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-[#4A4A4A] mb-2">Google Calendar</h2>
        <p className="text-gray-600">
          Conecta tu Google Calendar y cada reserva creará automáticamente un evento en tu
          calendario e invitará al alumno por email.
        </p>
      </div>

      {status.connected && status.token_valid === false ? (
        <div className="bg-white rounded-2xl border-2 border-[#F5A6A4] p-6 shadow-sm" data-testid="calendar-expired">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFF0EF] flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-[#E38987]" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#4A4A4A] text-lg mb-1">La conexión con Google ha caducado</h3>
              <p className="text-gray-600 text-sm mb-1">
                Cuenta: <span className="font-semibold">{status.email}</span>. Google ha invalidado el permiso, así que las
                nuevas reservas <strong>no</strong> se están añadiendo a tu calendario.
              </p>
              <p className="text-gray-500 text-xs mb-4">
                Suele ocurrir cuando la app de Google Cloud está en modo "Prueba" (los permisos caducan a los 7 días).
                Publica la app en Google Cloud Console → Pantalla de consentimiento OAuth → "Publicar aplicación" y vuelve a conectar.
              </p>
              <button
                onClick={handleConnect}
                disabled={connecting}
                data-testid="calendar-reconnect-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white font-semibold shadow hover:shadow-md transition-all disabled:opacity-60"
              >
                {connecting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirigiendo…</>
                ) : (
                  <><Link2 className="w-4 h-4" /> Volver a conectar con Google</>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : status.connected ? (
        <div className="bg-white rounded-2xl border-2 border-green-200 p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#4A4A4A] text-lg mb-1">Calendario conectado</h3>
              <p className="text-gray-600 text-sm mb-1">
                Cuenta: <span className="font-semibold">{status.email}</span>
              </p>
              {status.connected_at && (
                <p className="text-gray-400 text-xs mb-4">
                  Conectado el {new Date(status.connected_at).toLocaleString('es-ES')}
                </p>
              )}
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                data-testid="calendar-disconnect-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {disconnecting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Desconectando…</>
                ) : (
                  <><Unlink className="w-4 h-4" /> Desconectar</>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-[#A8D8EA]/60 p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#E8F5FA] flex items-center justify-center flex-shrink-0">
              <GCalIcon className="w-6 h-6 text-[#7DBDD9]" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#4A4A4A] text-lg mb-1">Aún no está conectado</h3>
              <p className="text-gray-600 text-sm mb-4">
                Autoriza el acceso a tu Google Calendar. Solo tendrás que hacerlo una vez.
              </p>
              <button
                onClick={handleConnect}
                disabled={connecting}
                data-testid="calendar-connect-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white font-semibold shadow hover:shadow-md transition-all disabled:opacity-60"
              >
                {connecting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirigiendo…</>
                ) : (
                  <><Link2 className="w-4 h-4" /> Conectar con Google</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#FFF7F6] rounded-2xl border border-[#F5A6A4]/40 p-5 text-sm text-[#4A4A4A]">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#F5A6A4] flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="font-semibold">Qué pasa cuando conectas tu calendario</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Cada nueva reserva crea un evento de 60 minutos en tu calendario principal.</li>
              <li>El alumno recibe automáticamente una invitación con recordatorios.</li>
              <li>El evento incluye el nombre del alumno, email, tipo de clase y mensaje.</li>
              <li>La zona horaria de los eventos es Europa/Madrid.</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
